import React from 'react';
import { motion } from 'framer-motion';
import { Package, Users, Building2, TrendingUp, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';

const DepartmentsPage = () => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      className="text-center py-6"
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
    >
      <motion.div
        className="mb-8"
        variants={cardVariants}
      >
        <img 
          src="/images/TITSA-LOGO-negro-sin-letras.png" 
          alt="TITSA LOGO" 
          className="mx-auto max-w-xs w-full h-auto object-contain"
        />
      </motion.div>
      <motion.h2
        className="text-4xl font-extrabold text-gray-800 mb-6 bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent"
        variants={cardVariants}
      >
        Sistema Empresarial - Departamentos
      </motion.h2>
      <motion.p
        className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto"
        variants={cardVariants}
      >
        Accede a los diferentes departamentos de la empresa. Sistema escalable y modular.
      </motion.p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
        {/* Tarjeta de Almacén */}
        <Link to="/almacen">
          <motion.div
            className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl shadow-lg p-8 h-72 flex flex-col items-center justify-center border-2 border-blue-200 hover:shadow-2xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-blue-500 rounded-full p-4 mb-4">
              <Package className="w-16 h-16 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Almacén</h3>
            <p className="text-gray-600 text-center text-sm px-2">
              Sistema completo de gestión de inventario, entradas, salidas y control de almacén.
            </p>
          </motion.div>
        </Link>

        {/* Tarjeta de Recursos Humanos */}
        <Link to="/recursos-humanos">
          <motion.div
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 h-72 flex flex-col items-center justify-center border-2 border-green-200 hover:shadow-2xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-green-500 rounded-full p-4 mb-4">
              <Users className="w-16 h-16 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">RRHH</h3>
            <p className="text-gray-600 text-center text-sm px-2">
              Gestión de empleados, expedientes, vacaciones, finiquitos y estadísticas de personal.
            </p>
          </motion.div>
        </Link>

        {/* Tarjeta de Gerenciamiento */}
        <motion.div
          className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 h-72 flex flex-col items-center justify-center border-2 border-purple-200 hover:shadow-2xl transition-all duration-300 cursor-pointer opacity-60"
          variants={cardVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="bg-purple-400 rounded-full p-4 mb-4">
            <TrendingUp className="w-16 h-16 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">Gerenciamiento</h3>
          <p className="text-gray-600 text-center text-sm px-2">
            Módulo de gestión gerencial y toma de decisiones. 
          </p>
          <span className="mt-3 text-xs bg-purple-200 text-purple-800 px-3 py-1 rounded-full font-semibold">
            Próximamente
          </span>
        </motion.div>

        {/* Tarjeta de Torre 2 */}
        <motion.div
          className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl shadow-lg p-8 h-72 flex flex-col items-center justify-center border-2 border-orange-200 hover:shadow-2xl transition-all duration-300 cursor-pointer opacity-60"
          variants={cardVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="bg-orange-400 rounded-full p-4 mb-4">
            <Building2 className="w-16 h-16 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">Torre 2</h3>
          <p className="text-gray-600 text-center text-sm px-2">
            Gestión y operaciones de la Torre 2 de la empresa.
          </p>
          <span className="mt-3 text-xs bg-orange-200 text-orange-800 px-3 py-1 rounded-full font-semibold">
            Próximamente
          </span>
        </motion.div>
      </div>

      {/* Sección informativa adicional */}
      <motion.div
        className="mt-16 max-w-4xl mx-auto"
        variants={cardVariants}
      >
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Layers className="w-8 h-8 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-800">Sistema Modular Escalable</h3>
          </div>
          <p className="text-gray-600 text-sm">
            Este sistema está diseñado para crecer junto con tu empresa. Cada departamento cuenta con sus propios módulos
            especializados que se integran perfectamente con el resto de la organización.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DepartmentsPage;
